// Weather-owned scenery module. Sibling to the home/zen sky (assistant_hero +
// _home_scene.scss): same visual vocabulary — a layered gradient sky with a
// horizon glow, a soft sun/moon disc, drifting cloud decks and a twinkling star
// field — but extended with a <canvas> particle engine for the things CSS spans
// can't do at 60fps: hundreds of rain streaks and snow flakes with parallax
// depth, plus a wind overlay. Nothing here imports or mutates any home/zen
// module; the weather controllers own it exclusively.
//
// The FAMILIES list and familyForCode() mirror Weather::Scene (Ruby) — the
// server is the source of truth for the WMO mapping; this is the client copy
// used by Emulate mode. Keep the two in lockstep.

export const FAMILIES = [
    "clear",
    "partly_cloudy",
    "overcast",
    "fog",
    "drizzle",
    "rain",
    "snow",
    "thunderstorm"
]

export const FAMILY_LABELS = {
    clear: "Clear",
    partly_cloudy: "Partly cloudy",
    overcast: "Overcast",
    fog: "Fog",
    drizzle: "Drizzle",
    rain: "Rain",
    snow: "Snow",
    thunderstorm: "Thunderstorm"
}

// WMO code -> family. Mirrors Weather::Scene::FAMILY_FOR_CODE.
export function familyForCode(code) {
    const c = parseInt(code, 10)
    if (Number.isNaN(c)) return "overcast"
    if (c === 0) return "clear"
    if (c === 1 || c === 2) return "partly_cloudy"
    if (c === 3) return "overcast"
    if (c === 45 || c === 48) return "fog"
    if (c >= 51 && c <= 57) return "drizzle"
    if ((c >= 61 && c <= 67) || (c >= 80 && c <= 82)) return "rain"
    if ((c >= 71 && c <= 77) || c === 85 || c === 86) return "snow"
    if (c >= 95 && c <= 99) return "thunderstorm"
    return "overcast"
}

// Per-family scene recipe. `particles` selects the canvas engine mode; the
// other flags toggle CSS layers (handled by class names in the controller).
// `lightning` arms the flash timer. Backdrop palettes live in _weather.scss,
// keyed on .wx-scene--<family> and .wx-scene--night.
export const SCENES = {
    clear: { particles: "none", stars: true, sun: true, clouds: 0, lightning: false },
    partly_cloudy: { particles: "none", stars: true, sun: true, clouds: 3, lightning: false },
    overcast: { particles: "none", stars: false, sun: false, clouds: 5, lightning: false },
    fog: { particles: "none", stars: false, sun: false, clouds: 2, lightning: false },
    drizzle: { particles: "drizzle", stars: false, sun: false, clouds: 4, lightning: false },
    rain: { particles: "rain", stars: false, sun: false, clouds: 5, lightning: false },
    snow: { particles: "snow", stars: false, sun: false, clouds: 4, lightning: false },
    thunderstorm: { particles: "rain", stars: false, sun: false, clouds: 5, lightning: true, dense: true }
}

export function sceneFor(family) {
    return SCENES[family] || SCENES.overcast
}

// Wind speed (km/h) above which we lean the rain/snow and add a faint wind
// streak overlay. Roughly a fresh breeze.
export const WIND_THRESHOLD = 24

function rand(min, max) {
    return min + Math.random() * (max - min)
}

// Canvas particle engine. One instance per scene; the controller owns the rAF
// loop and calls step(dtSeconds). Draws rain, drizzle, snow and an optional
// wind overlay. Pure 2D canvas, transform-free — cheap to composite.
export class WeatherParticles {
    constructor(canvas) {
        this.canvas = canvas
        this.ctx = canvas.getContext("2d")
        this.dpr = Math.min(window.devicePixelRatio || 1, 2)
        this.w = 0
        this.h = 0
        this.mode = "none"
        this.dense = false
        this.wind = 0 // -1..1 horizontal bias
        this.isDay = true
        this.drops = []
        this.flakes = []
        this.gusts = []
        this.resize()
    }

    resize() {
        const rect = this.canvas.getBoundingClientRect()
        this.w = Math.max(1, rect.width)
        this.h = Math.max(1, rect.height)
        this.canvas.width = Math.round(this.w * this.dpr)
        this.canvas.height = Math.round(this.h * this.dpr)
        this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
        this._seed()
    }

    // family: scene family; wind: km/h; isDay: bool. Rebuilds the particle set.
    configure({ family, wind = 0, isDay = true } = {}) {
        const scene = sceneFor(family)
        this.mode = scene.particles
        this.dense = !!scene.dense
        this.isDay = isDay
        // Map km/h to a gentle horizontal bias, clamped.
        this.windSpeed = wind
        this.wind = Math.max(-1, Math.min(1, wind / 60))
        this.windy = wind >= WIND_THRESHOLD
        this._seed()
    }

    _seed() {
        this.drops = []
        this.flakes = []
        this.gusts = []
        if (this.w <= 1 || this.h <= 1) return

        if (this.mode === "rain" || this.mode === "drizzle") {
            const drizzle = this.mode === "drizzle"
            let count = drizzle ? 90 : 220
            if (this.dense) count = 320
            count = Math.round(count * Math.min(1, (this.w * this.h) / (390 * 700)))
            for (let i = 0; i < count; i++) this.drops.push(this._newDrop(drizzle, true))
        } else if (this.mode === "snow") {
            let count = Math.round(140 * Math.min(1, (this.w * this.h) / (390 * 700)))
            for (let i = 0; i < count; i++) this.flakes.push(this._newFlake(true))
        }

        if (this.windy && this.mode !== "none") {
            const gustCount = 14
            for (let i = 0; i < gustCount; i++) this.gusts.push(this._newGust(true))
        }
    }

    _newDrop(drizzle, anywhere) {
        const depth = rand(0.5, 1) // near drops longer/faster/brighter
        return {
            x: rand(-40, this.w + 40),
            y: anywhere ? rand(-this.h, this.h) : rand(-40, 0),
            len: (drizzle ? rand(4, 9) : rand(10, 20)) * depth,
            speed: (drizzle ? rand(320, 460) : rand(620, 900)) * depth,
            depth,
            splash: 0
        }
    }

    _newFlake(anywhere) {
        const depth = rand(0.4, 1)
        return {
            x: rand(0, this.w),
            y: anywhere ? rand(0, this.h) : rand(-12, 0),
            r: rand(1.1, 3.2) * depth,
            speed: rand(24, 60) * depth,
            depth,
            sway: rand(0.4, 1.4),
            phase: rand(0, Math.PI * 2),
            spin: rand(0.4, 1.1)
        }
    }

    _newGust(anywhere) {
        const depth = rand(0.3, 1)
        return {
            x: anywhere ? rand(0, this.w) : rand(-120, -20),
            y: rand(0.1 * this.h, 0.7 * this.h),
            len: rand(40, 120) * depth,
            speed: rand(180, 360) * depth,
            depth,
            alpha: rand(0.03, 0.09)
        }
    }

    step(dt) {
        const ctx = this.ctx
        ctx.clearRect(0, 0, this.w, this.h)
        if (this.mode === "none" && !this.windy) return

        // Cap dt so a tab regaining focus doesn't teleport particles.
        dt = Math.min(dt, 0.05)

        if (this.mode === "rain" || this.mode === "drizzle") this._stepRain(dt)
        else if (this.mode === "snow") this._stepSnow(dt)
        if (this.windy) this._stepGusts(dt)
    }

    _stepRain(dt) {
        const ctx = this.ctx
        const drizzle = this.mode === "drizzle"
        const angle = this.wind * (drizzle ? 0.22 : 0.32) // horizontal lean
        ctx.lineCap = "round"
        for (const d of this.drops) {
            d.y += d.speed * dt
            d.x += d.speed * dt * angle
            const alpha = (drizzle ? 0.18 : 0.28) * (0.5 + d.depth * 0.5)
            ctx.strokeStyle = this.isDay
                ? `rgba(210,225,245,${alpha})`
                : `rgba(180,200,235,${alpha})`
            ctx.lineWidth = drizzle ? 0.8 : 1.1 * d.depth
            ctx.beginPath()
            ctx.moveTo(d.x, d.y)
            ctx.lineTo(d.x - d.len * angle, d.y - d.len)
            ctx.stroke()

            if (d.y > this.h) {
                // Brief splash tick near the ground for the near plane.
                if (!drizzle && d.depth > 0.75) this._splash(d.x, this.h - rand(2, 10), d.depth)
                Object.assign(d, this._newDrop(drizzle, false))
                d.x = rand(-40, this.w + 40)
            }
        }
    }

    _splash(x, y, depth) {
        const ctx = this.ctx
        ctx.strokeStyle = this.isDay ? "rgba(210,225,245,0.22)" : "rgba(180,200,235,0.18)"
        ctx.lineWidth = 1
        const r = 2.4 * depth
        ctx.beginPath()
        ctx.moveTo(x - r, y)
        ctx.quadraticCurveTo(x, y - r * 1.4, x + r, y)
        ctx.stroke()
    }

    _stepSnow(dt) {
        const ctx = this.ctx
        const drift = this.wind * 0.6
        for (const f of this.flakes) {
            f.phase += f.spin * dt
            f.y += f.speed * dt
            f.x += (Math.sin(f.phase) * f.sway * 14 + drift * f.speed) * dt
            const alpha = 0.55 * (0.45 + f.depth * 0.55)
            ctx.fillStyle = `rgba(255,255,255,${alpha})`
            ctx.beginPath()
            ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2)
            ctx.fill()

            if (f.y > this.h + 4) {
                Object.assign(f, this._newFlake(false))
                f.x = rand(0, this.w)
            } else if (f.x < -8) {
                f.x = this.w + 8
            } else if (f.x > this.w + 8) {
                f.x = -8
            }
        }
    }

    _stepGusts(dt) {
        const ctx = this.ctx
        const dir = this.wind >= 0 ? 1 : -1
        ctx.lineCap = "round"
        for (const g of this.gusts) {
            g.x += g.speed * dt * dir
            ctx.strokeStyle = this.isDay ? `rgba(255,255,255,${g.alpha})` : `rgba(200,215,245,${g.alpha})`
            ctx.lineWidth = 1.4 * g.depth
            ctx.beginPath()
            ctx.moveTo(g.x, g.y)
            ctx.quadraticCurveTo(g.x + g.len * 0.5 * dir, g.y - 6 * g.depth, g.x + g.len * dir, g.y)
            ctx.stroke()
            if ((dir > 0 && g.x > this.w + 140) || (dir < 0 && g.x < -140)) {
                Object.assign(g, this._newGust(false))
                g.x = dir > 0 ? rand(-140, -20) : rand(this.w + 20, this.w + 140)
            }
        }
    }

    clear() {
        this.ctx.clearRect(0, 0, this.w, this.h)
    }
}
